/**
 * My access (ADR-034): what the caller can do in this app here, decided by
 * the same resolver every request uses, why (the explainer), and asking for
 * more. Every member can see this; nobody sees anyone else's here.
 */
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/use-toast';
import type { MyAppAccessDto } from '@/lib/api';
import {
  useCloseAppAccessRequest,
  useRequestAppAccess,
  type AppAccessScope,
} from '../../hooks/useAppAccess';
import { AppText } from './AttributedText';
import { ExplainerPanel } from './ExplainerPanel';
import { describeAppAccessError, formatDate, permissionLabel, roleLabel } from './shared';

/** Mirrors `APP_ACCESS_REQUEST_MESSAGE_MAX` in `@fias/db-types`. */
const REQUEST_MESSAGE_MAX = 500;

interface MyAccessSectionProps {
  scope: AppAccessScope;
  appName: string;
  mine: MyAppAccessDto;
}

export function MyAccessSection({ scope, appName, mine }: MyAccessSectionProps) {
  const { catalog } = mine;
  // `activating`: roles are on and the app's older records are being placed.
  const rolesOn = mine.mode === 'roles' || mine.mode === 'activating';
  // The approval was revoked: the roles still held reach nothing, and asking
  // for more waits for it to be approved again.
  const admitted = mine.admitted !== false;
  const [explain, setExplain] = useState(false);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Your access to {appName}</CardTitle>
          <CardDescription>
            {rolesOn
              ? `In this workspace, ${appName}’s shared data follows its roles. Your workspace role is ${mine.tier}.`
              : mine.mode === 'legacy'
                ? `Roles are not turned on for ${appName} here yet, so its shared data follows your workspace role (${mine.tier}).`
                : `${appName} declares no roles. Its shared data follows your workspace role (${mine.tier}).`}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          {rolesOn && (
            <>
              {mine.roles.length === 0 ? (
                <p className="text-muted-foreground">You don’t hold any of its roles.</p>
              ) : (
                <ul className="space-y-1">
                  {mine.roles.map((r) => (
                    <li key={r.roleId}>
                      <span className="font-medium">
                        <AppText appName={appName}>{roleLabel(catalog.roles, r.roleId)}</AppText>
                      </span>
                      {r.expiresAt && (
                        <span className="text-muted-foreground">
                          {' '}
                          until {formatDate(r.expiresAt)}
                        </span>
                      )}
                      {!r.eligible && (
                        <span className="text-amber-700 dark:text-amber-300">
                          {' '}
                          {admitted
                            ? '— gives nothing while your workspace role is too low for it'
                            : '— gives nothing while the app is not approved here'}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              <p>
                <span className="font-medium">You can:</span>{' '}
                {mine.permissions.map((p) => permissionLabel(catalog.permissions, p)).join(', ') ||
                  'nothing in its shared data'}
              </p>
            </>
          )}
          <Button size="sm" variant="ghost" onClick={() => setExplain((v) => !v)}>
            {explain ? 'Hide details' : 'What can I do with each kind of data?'}
          </Button>
          {explain && (
            <ExplainerPanel
              scope={scope}
              principalId={mine.seat.principalId}
              who="you"
              appName={appName}
              roles={catalog.roles}
              permissions={catalog.permissions}
              audiences={catalog.audiences ?? []}
            />
          )}
        </CardContent>
      </Card>
      {rolesOn && (admitted || mine.openRequest) && (
        <RequestCard scope={scope} appName={appName} mine={mine} />
      )}
    </div>
  );
}

function RequestCard({ scope, appName, mine }: MyAccessSectionProps) {
  const request = useRequestAppAccess(scope);
  const withdraw = useCloseAppAccessRequest(scope);
  const [roleId, setRoleId] = useState('');
  const [message, setMessage] = useState('');
  const held = new Set(mine.roles.map((r) => r.roleId));
  const open = mine.openRequest;

  if (open) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Your request</CardTitle>
          <CardDescription>
            You asked for {open.roleId ? roleLabel(mine.catalog.roles, open.roleId) : 'access'} on{' '}
            {formatDate(open.createdAt)}. An access manager will answer it.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            size="sm"
            variant="outline"
            disabled={withdraw.isPending}
            onClick={() =>
              withdraw.mutate(open.requestId, {
                onError: (err) =>
                  toast({
                    title: 'Not withdrawn',
                    description: describeAppAccessError(err),
                    variant: 'destructive',
                  }),
              })
            }
          >
            Withdraw request
          </Button>
        </CardContent>
      </Card>
    );
  }
  // Not approved here: nothing to ask for until it is (an open request can
  // still be withdrawn, above).
  if (mine.admitted === false) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Ask for access</CardTitle>
        <CardDescription>
          This asks the app’s access managers. It gives you nothing until one of them answers.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-3 text-sm">
        <div className="grid gap-1.5">
          <Label htmlFor="request-role">Role (optional, from {appName})</Label>
          <select
            id="request-role"
            className="h-9 rounded-md border border-input bg-background px-2"
            value={roleId}
            onChange={(e) => setRoleId(e.target.value)}
          >
            <option value="">Any — let them decide</option>
            {mine.catalog.roles
              .filter((r) => !held.has(r.roleId))
              .map((r) => (
                <option key={r.roleId} value={r.roleId}>
                  {r.label}
                </option>
              ))}
          </select>
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="request-message">Why (optional)</Label>
          <Input
            id="request-message"
            value={message}
            maxLength={REQUEST_MESSAGE_MAX}
            onChange={(e) => setMessage(e.target.value)}
          />
        </div>
        <div>
          <Button
            size="sm"
            disabled={request.isPending}
            onClick={() =>
              request.mutate(
                {
                  ...(roleId ? { roleId } : {}),
                  ...(message.trim() ? { message: message.trim() } : {}),
                },
                {
                  onSuccess: () => toast({ title: 'Request sent' }),
                  onError: (err) =>
                    toast({
                      title: 'Not sent',
                      description: describeAppAccessError(err),
                      variant: 'destructive',
                    }),
                },
              )
            }
          >
            Ask for access
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
